import { Fragment } from 'react'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  EditButton,
  ReferenceField,
  DeleteButton,
  ReferenceInput,
  TextInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  usePermissions,
  useRecordContext,
  AutocompleteInput,
  FunctionField,
  SingleFieldList,
  ChipField,
  ReferenceArrayField,
} from 'react-admin'

import BookActionButton from '../../components/BookActionButton'
import BookStatusField from '../../components/BookStatusField'
import FilterComponent from '../../components/Filter'
import { ROLES } from '../../components/constants'

const Actions = props => {
  const { resource, currentSort, filterValues, exporter, basePath } = props
  return (
    <TopToolbar>
      <CreateButton basePath={basePath} />
      <ExportButton
        disabled={false}
        resource={resource}
        sort={currentSort}
        filter={filterValues}
        exporter={exporter}
        maxResults={10000}
      />
    </TopToolbar>
  )
}

const BookFilter = props => (
  <FilterComponent {...props}>
    <TextInput label="Search by title" source="title" alwaysOn />
    <ReferenceInput
      label="Publisher"
      source="publisherId"
      reference="users"
      filter={{ role: 'publisher' }}
      alwaysOn>
      <AutocompleteInput
        optionText="name"
        filterToQuery={searchText => ({
          name: { $iLike: `%${searchText}%` },
          role: ROLES.PUBLISHER,
        })}
      />
    </ReferenceInput>
    <ReferenceInput
      label="Author"
      source="authorIds"
      reference="authors"
      alwaysOn>
      <AutocompleteInput
        optionText="name"
        filterToQuery={searchText => ({
          name: { $iLike: `%${searchText}%` },
        })}
      />
    </ReferenceInput>
  </FilterComponent>
)

const ActionField = () => {
  const record = useRecordContext()
  const { permissions } = usePermissions()
  if (!record || permissions !== ROLES.ADMIN) return null

  switch (record.status) {
    case 'Under review':
      return (
        <Fragment>
          <BookActionButton
            label="Approve"
            action="approve"
            color="primary"
            record={record}
          />
          <BookActionButton
            label="Reject"
            action="reject"
            color="secondary"
            record={record}
          />
        </Fragment>
      )

    default:
      return <DeleteButton label="Delete" record={record} undoable={false} />
  }
}

const BookList = ({ ...props }) => {
  const { permissions } = usePermissions()

  function timeConversion(duration) {
    const portions = []

    const msInHour = 60 * 60
    const hours = Math.trunc(duration / msInHour)
    if (hours > 0) {
      portions.push(hours > 9 ? hours : `0${hours}`)
      duration = duration - hours * msInHour
    } else {
      portions.push('00')
    }

    const msInMinute = 60
    const minutes = Math.trunc(duration / msInMinute)
    if (minutes > 0) {
      portions.push(minutes > 9 ? minutes : `0${minutes}`)
      duration = duration - minutes * msInMinute
    } else {
      portions.push('00')
    }

    const seconds = Math.trunc(duration)
    if (seconds > 0) {
      portions.push(seconds > 9 ? seconds : `0${seconds}`)
    } else {
      portions.push('00')
    }

    return portions.join(':')
  }

  return (
    <List
      {...props}
      filters={<BookFilter />}
      actions={<Actions />}
      sort={{ field: 'updatedAt', order: 'DESC' }}>
      <Datagrid rowClick={false} bulkActionButtons={false}>
        <TextField source="id" />
        <TextField source="title" />
        <ReferenceArrayField
          reference="authors"
          source="authorIds"
          label="Author">
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <ReferenceField
          source="publisherId"
          reference="users"
          label="Publisher">
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          render={value => `${timeConversion(value.duration || 0)}`} // Convert duration from seconds to HH:mm:ss
          source="duration"
          label="Duration"
        />
        <DateField source="datePublished" label="Publish Date" />
        <ReferenceField label="Created by" source="createdBy" reference="users">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Updated by" source="updatedBy" reference="users">
          <TextField source="name" />
        </ReferenceField>
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
        <BookStatusField
          disabled={permissions !== ROLES.ADMIN && permissions !== ROLES.EDITOR}
        />
        <EditButton />
        <ActionField />
      </Datagrid>
    </List>
  )
}

export default BookList
